import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
  Typography
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useConfig } from "../../contexts/ConfigContext";
import { useOptions } from "../../contexts/OptionsContext";
import {
  AnchorType,
  buildAnchorRelationshipsPatchPayload,
  IAnchorRelationshipsPatchPayload,
  ICmTableDataRow
} from "../../services/cmTables";
import { ApiEndpoint, INormalisedRelations, RelationshipEditorState } from "../../types";
import { useDialogEditorState, useRelationshipEditorActions } from "../../store/relationshipEditor";
import RelationshipEditorTable from "./RelationshipEditorTable";

// ─── Types ────────────────────────────────────────────────────────────────────

export interface RelationshipEditDialogProps {
  open: boolean;
  onClose: () => void;
  /** The row being edited. Must have `id`, `name`, `description`, and `relationships` (normalised), reflecting the server's last-known state (the diff baseline). */
  item: ICmTableDataRow & { name: string; description: string };
  endpoint: ApiEndpoint;
  /**
   * Seeds the editor with relationships carried over from a previous, unsaved
   * dialog session (so the user's earlier picks remain visible on reopen).
   * The diff is always computed against `item.relationships`, not this value.
   */
  initialEditorState?: RelationshipEditorState;
  /**
   * Called when the user clicks "Save Changes". No API call is made here —
   * the parent (EntityForm) holds onto `editorState`/`payload` and applies the
   * patch when the overall form is saved, so the user can still cancel.
   */
  onApply: (editorState: RelationshipEditorState, payload: IAnchorRelationshipsPatchPayload) => void;
  /** Current selection of restriction ids — `restrictions` is a plain field on the row/media item, not a relationship. */
  restrictionIds: number[];
  /** Called immediately when the user saves a new restrictions selection in the table's Restrictions column. */
  onRestrictionsChange: (ids: number[]) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function resolveAnchorType(endpoint: ApiEndpoint): AnchorType {
  if (endpoint.entityType === "media") return "media";
  if (endpoint.entityType === "spatial-dataset") return "spatialdataset";
  return "cmtabledata";
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function RelationshipEditDialog({
  open,
  onClose,
  item,
  endpoint,
  initialEditorState,
  onApply,
  restrictionIds,
  onRestrictionsChange
}: RelationshipEditDialogProps) {
  const config = useConfig();
  const { loadOptionsForEndpoint } = useOptions();
  const { enqueueSnackbar } = useSnackbar();

  const { anchorId, anchorType, originalState, editorState } = useDialogEditorState();
  const { initDialog, setDialogTableItems, resetDialog } = useRelationshipEditorActions();

  const [isRefreshing, setIsRefreshing] = useState(false);

  // ── Init / cleanup ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (open && item) {
      const relationships: INormalisedRelations = (item.relationships as INormalisedRelations) ??
        (item.relations as INormalisedRelations) ?? {
          byTableSlug: {},
          media: []
        };
      initDialog(Number(item.id), resolveAnchorType(endpoint), relationships, initialEditorState);
    }
    if (!open) {
      resetDialog();
    }
  }, [open, item?.id]);

  // ── Refresh options ─────────────────────────────────────────────────────────

  const handleRefreshOptions = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      const toLoad = config.apiEndpoints.filter((ep) => ep.tableName !== endpoint.tableName).map((ep) => ep.tableName);
      await loadOptionsForEndpoint(toLoad);
      enqueueSnackbar("Options refreshed", { variant: "success" });
    } catch {
      enqueueSnackbar("Failed to refresh options", { variant: "error" });
    } finally {
      setIsRefreshing(false);
    }
  };

  // ── Apply ───────────────────────────────────────────────────────────────────

  const handleApply = () => {
    if (!anchorId) return;
    const payload = buildAnchorRelationshipsPatchPayload(originalState, editorState);
    onApply(editorState, payload);
    onClose();
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Dialog open={open} onClose={onClose} maxWidth='lg' fullWidth scroll='paper'>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography variant='h6' component='span'>
          Edit Relationships — {item?.name}
        </Typography>
        <Stack direction='row' gap={0.5} alignItems='center'>
          <Tooltip title='Refresh relationship options'>
            <span>
              <IconButton
                onClick={handleRefreshOptions}
                size='small'
                disabled={isRefreshing}
                sx={{
                  color: "primary.main",
                  "&:hover": { backgroundColor: "primary.light", color: "primary.contrastText" },
                  "&:disabled": { color: "action.disabled" }
                }}
              >
                {isRefreshing ? <CircularProgress size={16} /> : <RefreshIcon fontSize='small' />}
              </IconButton>
            </span>
          </Tooltip>
          <IconButton onClick={onClose} size='small'>
            <CloseIcon fontSize='small' />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: 1, minHeight: 300 }}>
        {anchorId !== null ? (
          <RelationshipEditorTable
            editorState={editorState}
            anchorId={anchorId}
            anchorName={item?.name ?? ""}
            anchorDescription={item?.description ?? ""}
            anchorTableSlug={endpoint.tableName}
            showMediaColumn={true}
            onTableSlugChange={setDialogTableItems}
            isLoading={isRefreshing}
            restrictionIds={restrictionIds}
            onRestrictionsChange={onRestrictionsChange}
          />
        ) : (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2 }}>
        <Button onClick={onClose} variant='secondary-outlined'>
          Cancel
        </Button>
        <Button onClick={handleApply} variant='contained' disableElevation disabled={anchorId === null}>
          Save Changes
        </Button>
      </DialogActions>
    </Dialog>
  );
}
